import {
  type BillingEntityOption,
  type CreateBillingEntity,
  type UpdateBillingEntitySettings,
} from '@utazzo/shared';
import { UzBadge, UzButton, UzCheckbox, UzInput } from '@utazzo/ui';
import { ArrowLeft, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { resolveMedia } from '../website-cms/components/media.api';
import { MediaDropField } from '../website-cms/components/MediaDropField';

import { BankAccountsSettings } from './BankAccountsSettings';
import {
  useBillingEntities,
  useBillingEntityById,
  useCreateBillingEntity,
  useSetDefaultBillingEntity,
  useUpdateBillingEntityById,
} from './use-settings';

/**
 * Company / Invoice settings tab - multi GST-account manager.
 *
 * Lists every billing entity (seller identity / GST account) printed on
 * invoices; each row can be made the default or edited. Add opens the same
 * company-fields form with an extra short `code`. Saving is ADMIN-only; a caller
 * without write sees the fields read-only.
 */

const Field = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}): React.JSX.Element => (
  <div>
    <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[12px] text-(--fg-faint)">{hint}</p>}
  </div>
);

const opt = (v: string): string | null => (v.trim() ? v.trim() : null);

/**
 * The company-fields form, reused for both Edit (`entityId` set) and Add
 * (`entityId` null). Edit hydrates from the fetched entity; Add starts blank
 * and collects the extra `code` plus an optional "make default" checkbox.
 */
const BillingEntityForm = ({
  entityId,
  canWrite,
  onDone,
}: {
  entityId: string | null;
  canWrite: boolean;
  onDone: () => void;
}): React.JSX.Element => {
  const isEdit = entityId != null;
  const { data, isLoading } = useBillingEntityById(entityId);
  const create = useCreateBillingEntity();
  const update = useUpdateBillingEntityById();

  const [code, setCode] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [legalName, setLegalName] = useState('');
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');
  const [cin, setCin] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('');
  const [hsnDefault, setHsnDefault] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  // Transient id of a freshly uploaded logo; resolved to its CDN url into logoUrl.
  const [logoMediaId, setLogoMediaId] = useState<string | null>(null);
  const [sealUrl, setSealUrl] = useState('');
  const [sealMediaId, setSealMediaId] = useState<string | null>(null);
  const [terms, setTerms] = useState('');

  const onLogoUpload = (mediaId: string | null): void => {
    setLogoMediaId(mediaId);
    if (!mediaId) {
      setLogoUrl('');
      return;
    }
    void resolveMedia(mediaId)
      .then((m) => setLogoUrl(m.url))
      .catch(() => {
        /* preview/url resolution best-effort */
      });
  };

  const onSealUpload = (mediaId: string | null): void => {
    setSealMediaId(mediaId);
    if (!mediaId) {
      setSealUrl('');
      return;
    }
    void resolveMedia(mediaId)
      .then((m) => setSealUrl(m.url))
      .catch(() => {
        /* preview/url resolution best-effort */
      });
  };

  useEffect(() => {
    if (!data) return;
    setLegalName(data.legalName ?? '');
    setGstin(data.gstin ?? '');
    setPan(data.pan ?? '');
    setCin(data.cin ?? '');
    setAddressLine1(data.addressLine1 ?? '');
    setAddressLine2(data.addressLine2 ?? '');
    setCity(data.city ?? '');
    setPostalCode(data.postalCode ?? '');
    setPhone(data.phone ?? '');
    setEmail(data.email ?? '');
    setInvoicePrefix(data.invoicePrefix ?? '');
    setHsnDefault(data.hsnDefault ?? '');
    setLogoUrl(data.logoUrl ?? '');
    setLogoMediaId(null);
    setSealUrl(data.sealUrl ?? '');
    setSealMediaId(null);
    setTerms(data.terms ?? '');
  }, [data]);

  if (isEdit && (isLoading || !data)) {
    return <p className="p-6 text-sm text-(--fg-faint)">Loading...</p>;
  }

  const onSave = (): void => {
    // Required fields must be non-empty (mirrors the shared schemas); code is
    // additionally required when creating.
    if (!legalName.trim() || !gstin.trim() || !invoicePrefix.trim() || !hsnDefault.trim()) {
      return;
    }
    if (!isEdit && !code.trim()) return;

    const base: UpdateBillingEntitySettings = {
      legalName: legalName.trim(),
      gstin: gstin.trim(),
      pan: opt(pan),
      cin: opt(cin),
      logoUrl: opt(logoUrl),
      sealUrl: opt(sealUrl),
      terms: opt(terms),
      addressLine1: opt(addressLine1),
      addressLine2: opt(addressLine2),
      city: opt(city),
      postalCode: opt(postalCode),
      phone: opt(phone),
      email: opt(email),
      invoicePrefix: invoicePrefix.trim(),
      hsnDefault: hsnDefault.trim(),
    };

    if (isEdit) {
      update.mutate({ id: entityId, body: base }, { onSuccess: onDone });
    } else {
      const body: CreateBillingEntity = {
        ...base,
        code: code.trim(),
        ...(makeDefault ? { isDefault: true } : {}),
      };
      create.mutate(body, { onSuccess: onDone });
    }
  };

  const saving = create.isPending || update.isPending;

  return (
    <div className="flex max-w-2xl flex-col gap-6 py-6">
      <div className="flex items-center gap-3">
        <UzButton
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={onDone}
        >
          Back to GST accounts
        </UzButton>
      </div>

      <div className="rounded-md border border-(--border) bg-(--z-50) p-3 text-xs text-(--fg-muted)">
        This is a seller identity printed on invoices and credit notes. GSTIN, the invoice number
        prefix and the default HSN are used by the invoicing engine, so change them with care.
      </div>

      {!isEdit && (
        <Field
          label="Code"
          hint="Required. A short unique code for this GST account (e.g. HO, MUM)."
        >
          <UzInput value={code} onChange={(e) => setCode(e.target.value)} className="font-mono" />
        </Field>
      )}

      <Field label="Legal name" hint="Required. The registered company name on the invoice.">
        <UzInput value={legalName} onChange={(e) => setLegalName(e.target.value)} />
      </Field>

      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <legend className="mb-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
          Tax identifiers
        </legend>
        <Field label="GSTIN" hint="Required.">
          <UzInput value={gstin} onChange={(e) => setGstin(e.target.value)} className="font-mono" />
        </Field>
        <Field label="PAN">
          <UzInput value={pan} onChange={(e) => setPan(e.target.value)} className="font-mono" />
        </Field>
        <Field label="CIN">
          <UzInput value={cin} onChange={(e) => setCin(e.target.value)} className="font-mono" />
        </Field>
      </fieldset>

      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <legend className="mb-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
          Registered address
        </legend>
        <Field label="Address line 1">
          <UzInput value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
        </Field>
        <Field label="Address line 2">
          <UzInput value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
        </Field>
        <Field label="City">
          <UzInput value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
        <Field label="Postal code">
          <UzInput value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
        </Field>
      </fieldset>

      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <legend className="mb-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
          Contact
        </legend>
        <Field label="Phone">
          <UzInput value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="Email">
          <UzInput value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </fieldset>

      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <legend className="mb-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
          Invoicing
        </legend>
        <Field label="Invoice prefix" hint="Required. Prepended to every invoice number.">
          <UzInput
            value={invoicePrefix}
            onChange={(e) => setInvoicePrefix(e.target.value)}
            className="font-mono"
          />
        </Field>
        <Field label="Default HSN" hint="Required. Used when a line has no HSN of its own.">
          <UzInput
            value={hsnDefault}
            onChange={(e) => setHsnDefault(e.target.value)}
            className="font-mono"
          />
        </Field>
      </fieldset>

      <Field
        label="Logo"
        hint="Optional. Uploaded and served through the CDN; shown on the invoice. Leave blank to fall back to the app brand logo."
      >
        {logoUrl && !logoMediaId && (
          <img src={logoUrl} alt="Current logo" className="mb-2 h-12 max-w-60 object-contain" />
        )}
        <MediaDropField value={logoMediaId} kind="image" onChange={onLogoUpload} />
      </Field>

      <Field
        label="Company seal / stamp"
        hint="Optional. Round-stamp image printed near the signature box on the invoice and credit note."
      >
        {sealUrl && !sealMediaId && (
          <img src={sealUrl} alt="Current seal" className="mb-2 h-24 w-24 object-contain" />
        )}
        <MediaDropField value={sealMediaId} kind="image" onChange={onSealUpload} />
      </Field>

      <Field
        label="Terms & conditions"
        hint="Optional. Printed at the bottom of the invoice and credit note. One point per line."
      >
        <textarea
          value={terms}
          onChange={(e) => setTerms(e.target.value)}
          rows={5}
          placeholder={
            '1. Payment due within the agreed credit period.\n2. Bookings are subject to supplier terms.\n3. Disputes subject to jurisdiction as per company policy.'
          }
          className="border-input bg-background text-foreground focus-visible:border-ring w-full rounded-md border px-3 py-2 text-sm transition-colors focus-visible:outline-none"
        />
      </Field>

      {!isEdit && (
        <UzCheckbox
          label="Make this the default GST account"
          description="New invoices default to this account."
          checked={makeDefault}
          onCheckedChange={(c) => setMakeDefault(Boolean(c))}
        />
      )}

      <div className="flex items-center gap-3 pt-2">
        <UzButton onClick={onSave} loading={saving} disabled={!canWrite}>
          {isEdit ? 'Save GST account' : 'Create GST account'}
        </UzButton>
        {!canWrite && (
          <span className="text-xs text-(--fg-faint)">Read-only - you can view but not save.</span>
        )}
      </div>
    </div>
  );
};

/** The list of GST accounts with default / edit actions. */
const BillingEntityList = ({
  entities,
  canWrite,
  onEdit,
  onAdd,
}: {
  entities: readonly BillingEntityOption[];
  canWrite: boolean;
  onEdit: (id: string) => void;
  onAdd: () => void;
}): React.JSX.Element => {
  const setDefault = useSetDefaultBillingEntity();

  return (
    <div className="flex max-w-2xl flex-col gap-4 py-6">
      {/* Phones: the note takes the full width and the button sits under it. */}
      <div className="flex items-center justify-between max-sm:flex-col max-sm:items-stretch max-sm:gap-3">
        <div className="rounded-md border border-(--border) bg-(--z-50) p-3 text-xs text-(--fg-muted)">
          These are the seller identities (GST accounts) printed on invoices. New invoices default
          to the account marked Default.
        </div>
        <UzButton
          className="max-sm:self-end"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={onAdd}
          disabled={!canWrite}
        >
          Add GST account
        </UzButton>
      </div>

      {entities.length === 0 ? (
        <p className="text-sm text-(--fg-faint)">No GST accounts yet. Add one to raise invoices.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {entities.map((e) => (
            <li
              key={e.id}
              className="flex items-center gap-3 rounded-md border border-(--border) bg-(--z-0) p-3"
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="flex items-center gap-2 text-sm font-medium text-(--fg) max-sm:flex-wrap max-sm:gap-y-1">
                  {e.legalName}
                  {e.isDefault && <UzBadge tone="success">Default</UzBadge>}
                </span>
                <span className="truncate font-mono text-xs text-(--fg-muted)">{e.gstin}</span>
              </div>
              {!e.isDefault && (
                <UzButton
                  variant="ghost"
                  size="sm"
                  loading={setDefault.isPending && setDefault.variables === e.id}
                  disabled={!canWrite}
                  onClick={() => setDefault.mutate(e.id)}
                >
                  Set as default
                </UzButton>
              )}
              <UzButton variant="outline" size="sm" onClick={() => onEdit(e.id)}>
                Edit
              </UzButton>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const CompanySettingsTab = ({
  canWrite = true,
}: {
  canWrite?: boolean;
}): React.JSX.Element => {
  const { data: entities, isLoading } = useBillingEntities();
  // null = list view; { id: null } = add form; { id: string } = edit form.
  const [editing, setEditing] = useState<{ id: string | null } | null>(null);

  if (isLoading || !entities) {
    return <p className="p-6 text-sm text-(--fg-faint)">Loading...</p>;
  }

  if (editing) {
    return (
      <BillingEntityForm
        entityId={editing.id}
        canWrite={canWrite}
        onDone={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <BillingEntityList
        entities={entities}
        canWrite={canWrite}
        onEdit={(id) => setEditing({ id })}
        onAdd={() => setEditing({ id: null })}
      />
      <BankAccountsSettings canWrite={canWrite} />
    </div>
  );
};
